import React, { useEffect, useMemo, useState } from "react";
import {
	Button,
	Card,
	Col,
	Container,
	Form,
	Offcanvas,
	Row,
	Table,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import { getEvents } from "../../services/eventService";
import { getLeagues } from "../../services/leagueService";
import { getSeasons } from "../../services/seasonService";
import { getSeries, getSports } from "../../services/series";
import {
	createPrize,
	deletePrize,
	getManageablePrizes,
	PrizePayload,
	PrizeRecord,
	PrizeType,
	updatePrize,
	uploadPrizeImage,
} from "../../services/prizeService";

type ActiveFilter = "all" | "active" | "inactive";

interface PrizeFormState {
	league: string;
	prizeType: PrizeType;
	event: string;
	title: string;
	description: string;
	linkUrl: string;
	image: { url: string; key: string };
	winnerSnapshot: string;
	winnerLabel: string;
	isActive: boolean;
}

const initialForm: PrizeFormState = {
	league: "",
	prizeType: "season",
	event: "",
	title: "",
	description: "",
	linkUrl: "",
	image: { url: "", key: "" },
	winnerSnapshot: "",
	winnerLabel: "",
	isActive: true,
};

const getId = (value: any) => {
	if (!value) return "";
	if (typeof value === "string") return value;
	return value?._id || "";
};

const getName = (value: any, fallback = "") => {
	if (!value) return fallback;
	if (typeof value === "string") return fallback;
	return value?.name || fallback;
};

const AdminPrizesPage = () => {
	const [searchParams] = useSearchParams();
	const initialLeagueFilter = searchParams.get("leagueId") || "";

	const [prizes, setPrizes] = useState<PrizeRecord[]>([]);
	const [leagues, setLeagues] = useState<any[]>([]);
	const [events, setEvents] = useState<any[]>([]);
	const [sports, setSports] = useState<any[]>([]);
	const [series, setSeries] = useState<any[]>([]);
	const [seasons, setSeasons] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [showDrawer, setShowDrawer] = useState(false);
	const [selectedPrize, setSelectedPrize] = useState<PrizeRecord | null>(null);
	const [form, setForm] = useState<PrizeFormState>({
		...initialForm,
		league: initialLeagueFilter,
	});
	const [searchQuery, setSearchQuery] = useState("");
	const [filterLeague, setFilterLeague] = useState(initialLeagueFilter);
	const [filterType, setFilterType] = useState<PrizeType | "all">("all");
	const [filterActive, setFilterActive] = useState<ActiveFilter>("active");
	const [drawerLeagueSearch, setDrawerLeagueSearch] = useState("");
	const [drawerLeagueSport, setDrawerLeagueSport] = useState("");
	const [drawerLeagueSeries, setDrawerLeagueSeries] = useState("");
	const [drawerLeagueSeason, setDrawerLeagueSeason] = useState("");

	const selectedLeague = useMemo(
		() => leagues.find((league) => getId(league) === form.league),
		[leagues, form.league],
	);

	const filteredEventsForLeague = useMemo(() => {
		if (!selectedLeague) {
			return [];
		}
		const leagueSport = getId(selectedLeague.sport);
		const leagueSeries = getId(selectedLeague.series);
		const leagueSeason = getId(selectedLeague.season);

		return events
			.filter((event) => {
				const eventSport = getId(event.sport);
				const eventSeries = getId(event.series);
				const eventSeason = getId(event.season);
				if (leagueSport && eventSport !== leagueSport) return false;
				if (leagueSeries && eventSeries !== leagueSeries) return false;
				if (leagueSeason && eventSeason !== leagueSeason) return false;
				return true;
			})
			.sort(
				(a, b) =>
					new Date(a.date).getTime() - new Date(b.date).getTime(),
			);
	}, [events, selectedLeague]);

	const filteredLeaguesForDrawer = useMemo(() => {
		const query = drawerLeagueSearch.trim().toLowerCase();
		return leagues
			.filter((league) => {
				const leagueName = String(league?.name || "").toLowerCase();
				const leagueSportId = getId(league?.sport);
				const leagueSeriesId = getId(league?.series);
				const leagueSeasonId = getId(league?.season);

				if (query && !leagueName.includes(query)) return false;
				if (drawerLeagueSport && leagueSportId !== drawerLeagueSport) return false;
				if (drawerLeagueSeries && leagueSeriesId !== drawerLeagueSeries)
					return false;
				if (drawerLeagueSeason && leagueSeasonId !== drawerLeagueSeason)
					return false;

				return true;
			})
			.sort((a, b) =>
				String(a?.name || "").localeCompare(String(b?.name || "")),
			);
	}, [
		leagues,
		drawerLeagueSearch,
		drawerLeagueSport,
		drawerLeagueSeries,
		drawerLeagueSeason,
	]);

	const fetchPrizes = async () => {
		try {
			setLoading(true);
			const response = await getManageablePrizes({
				page: 1,
				limit: 200,
				leagueId: filterLeague || undefined,
				prizeType: filterType === "all" ? undefined : filterType,
				active:
					filterActive === "all"
						? undefined
						: filterActive === "active"
							? true
							: false,
				q: searchQuery || undefined,
			});
			setPrizes(response.data?.items || []);
		} catch (error) {
			console.error("Failed to fetch prizes:", error);
			setPrizes([]);
		} finally {
			setLoading(false);
		}
	};

	const fetchReferenceData = async () => {
		try {
			const [leagueRes, eventRes, sportRes, seriesRes, seasonRes] =
				await Promise.all([
					getLeagues(),
					getEvents(),
					getSports(),
					getSeries(),
					getSeasons(),
				]);
			setLeagues(leagueRes?.data || []);
			setEvents(eventRes?.data || []);
			setSports(sportRes?.data || []);
			setSeries(seriesRes?.data || []);
			setSeasons(seasonRes?.data || []);
		} catch (error) {
			console.error("Failed to fetch prize reference data:", error);
		}
	};

	useEffect(() => {
		void fetchReferenceData();
	}, []);

	useEffect(() => {
		void fetchPrizes();
	}, [filterLeague, filterType, filterActive, searchQuery]);

	const openCreateDrawer = () => {
		setSelectedPrize(null);
		setForm({
			...initialForm,
			league: filterLeague || initialLeagueFilter || "",
		});
		setDrawerLeagueSearch("");
		setDrawerLeagueSport("");
		setDrawerLeagueSeries("");
		setDrawerLeagueSeason("");
		setShowDrawer(true);
	};

	const openEditDrawer = (prize: PrizeRecord) => {
		const league = leagues.find((item) => getId(item) === getId(prize.league));
		setSelectedPrize(prize);
		setForm({
			league: getId(prize.league),
			prizeType: prize.prizeType,
			event: getId(prize.event),
			title: prize.title || "",
			description: prize.description || "",
			linkUrl: prize.linkUrl || "",
			image: {
				url: prize.image?.url || "",
				key: prize.image?.key || "",
			},
			winnerSnapshot: getId(prize.winnerSnapshot),
			winnerLabel: prize.winnerLabel || "",
			isActive: Boolean(prize.isActive),
		});
		setDrawerLeagueSearch(league?.name || "");
		setDrawerLeagueSport(getId(league?.sport));
		setDrawerLeagueSeries(getId(league?.series));
		setDrawerLeagueSeason(getId(league?.season));
		setShowDrawer(true);
	};

	const closeDrawer = () => {
		setShowDrawer(false);
	};

	const handleUploadImage = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const file = event.target.files?.[0];
		if (!file) return;
		if (!form.league) {
			alert("Choose a league before uploading an image.");
			return;
		}
		try {
			const response = await uploadPrizeImage(file, form.league);
			setForm((prev) => ({
				...prev,
				image: {
					url: response.data?.url || "",
					key: response.data?.key || "",
				},
			}));
		} catch (error: any) {
			console.error("Failed to upload prize image:", error);
			alert(
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to upload prize image.",
			);
		} finally {
			event.target.value = "";
		}
	};

	const handleSave = async () => {
		if (!form.league) {
			alert("League is required.");
			return;
		}
		if (!form.title.trim()) {
			alert("Title is required.");
			return;
		}
		if (!form.description.trim()) {
			alert("Description is required.");
			return;
		}
		if (form.prizeType === "round" && !form.event) {
			alert("Round prizes require an event.");
			return;
		}

		const payload: PrizePayload = {
			league: form.league,
			prizeType: form.prizeType,
			event: form.prizeType === "round" ? form.event : null,
			title: form.title.trim(),
			description: form.description.trim(),
			linkUrl: form.linkUrl.trim(),
			image: form.image,
			winnerSnapshot: form.winnerSnapshot.trim() || null,
			winnerLabel: form.winnerLabel.trim(),
			isActive: form.isActive,
		};

		try {
			setSaving(true);
			if (selectedPrize?._id) {
				await updatePrize(selectedPrize._id, payload);
			} else {
				await createPrize(payload);
			}
			await fetchPrizes();
			setShowDrawer(false);
		} catch (error: any) {
			console.error("Failed to save prize:", error);
			alert(
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to save prize.",
			);
		} finally {
			setSaving(false);
		}
	};

	const handleDeactivate = async () => {
		if (!selectedPrize?._id) {
			return;
		}
		if (!window.confirm("Deactivate this prize?")) {
			return;
		}
		try {
			await deletePrize(selectedPrize._id);
			await fetchPrizes();
			setShowDrawer(false);
		} catch (error: any) {
			console.error("Failed to deactivate prize:", error);
			alert(
				typeof error?.response?.data === "string"
					? error.response.data
					: "Failed to deactivate prize.",
			);
		}
	};

	return (
		<AdminLayout>
			<Container className="py-3">
				<Row className="align-items-end g-3 mb-3">
					<Col md={3}>
						<Form.Group controlId="prizeSearch">
							<Form.Label>Search</Form.Label>
							<Form.Control
								value={searchQuery}
								onChange={(event) => setSearchQuery(event.target.value)}
								placeholder="Title, description, winner"
							/>
						</Form.Group>
					</Col>
					<Col md={3}>
						<Form.Group controlId="prizeLeagueFilter">
							<Form.Label>League</Form.Label>
							<Form.Select
								value={filterLeague}
								onChange={(event) => setFilterLeague(event.target.value)}
							>
								<option value="">All leagues</option>
								{leagues.map((league) => (
									<option key={getId(league)} value={getId(league)}>
										{league?.name || "League"}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group controlId="prizeTypeFilter">
							<Form.Label>Type</Form.Label>
							<Form.Select
								value={filterType}
								onChange={(event) =>
									setFilterType(event.target.value as PrizeType | "all")
								}
							>
								<option value="all">All</option>
								<option value="season">Season</option>
								<option value="round">Round</option>
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group controlId="prizeActiveFilter">
							<Form.Label>Status</Form.Label>
							<Form.Select
								value={filterActive}
								onChange={(event) =>
									setFilterActive(event.target.value as ActiveFilter)
								}
							>
								<option value="active">Active</option>
								<option value="inactive">Inactive</option>
								<option value="all">All</option>
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md={2} className="d-grid">
						<Button onClick={openCreateDrawer}>Create Prize</Button>
					</Col>
				</Row>

				<Card>
					<Card.Body className="p-0">
						<Table responsive hover className="mb-0 align-middle">
							<thead>
								<tr>
									<th>Title</th>
									<th>Type</th>
									<th>League</th>
									<th>Event</th>
									<th>Status</th>
									<th>Updated</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan={7}>Loading prizes...</td>
									</tr>
								) : prizes.length === 0 ? (
									<tr>
										<td colSpan={7}>No prizes found.</td>
									</tr>
								) : (
									prizes.map((prize) => (
										<tr key={prize._id} onClick={() => openEditDrawer(prize)}>
											<td>{prize.title}</td>
											<td className="text-capitalize">{prize.prizeType}</td>
											<td>{getName(prize.league, "-")}</td>
											<td>{getName(prize.event, "-")}</td>
											<td>{prize.isActive ? "Active" : "Inactive"}</td>
											<td>
												{new Date(prize.updatedAt).toLocaleDateString()}
											</td>
											<td>
												<Button
													size="sm"
													variant="outline-primary"
													onClick={(event) => {
														event.stopPropagation();
														openEditDrawer(prize);
													}}
												>
													Edit
												</Button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</Table>
					</Card.Body>
				</Card>
			</Container>

			<Offcanvas
				show={showDrawer}
				onHide={closeDrawer}
				placement="end"
				className="admin-offcanvas admin-offcanvas--blog"
			>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title>
						{selectedPrize ? "Edit Prize" : "Create Prize"}
					</Offcanvas.Title>
				</Offcanvas.Header>
				<Offcanvas.Body>
					<Card className="mb-3">
						<Card.Body>
							<Row className="g-2">
								<Col xs={12}>
									<Form.Group controlId="drawerLeagueSearch">
										<Form.Label>Search League Name</Form.Label>
										<Form.Control
											value={drawerLeagueSearch}
											onChange={(event) =>
												setDrawerLeagueSearch(event.target.value)
											}
											placeholder="Type league name..."
										/>
									</Form.Group>
								</Col>
								<Col md={4}>
									<Form.Group controlId="drawerLeagueSeasonFilter">
										<Form.Label>Season</Form.Label>
										<Form.Select
											value={drawerLeagueSeason}
											onChange={(event) =>
												setDrawerLeagueSeason(event.target.value)
											}
										>
											<option value="">All seasons</option>
											{seasons.map((season) => (
												<option key={getId(season)} value={getId(season)}>
													{season?.name || "Season"}
												</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>
								<Col md={4}>
									<Form.Group controlId="drawerLeagueSeriesFilter">
										<Form.Label>Series</Form.Label>
										<Form.Select
											value={drawerLeagueSeries}
											onChange={(event) =>
												setDrawerLeagueSeries(event.target.value)
											}
										>
											<option value="">All series</option>
											{series.map((seriesItem) => (
												<option key={getId(seriesItem)} value={getId(seriesItem)}>
													{seriesItem?.name || "Series"}
												</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>
								<Col md={4}>
									<Form.Group controlId="drawerLeagueSportFilter">
										<Form.Label>Discipline</Form.Label>
										<Form.Select
											value={drawerLeagueSport}
											onChange={(event) =>
												setDrawerLeagueSport(event.target.value)
											}
										>
											<option value="">All disciplines</option>
											{sports.map((sport) => (
												<option key={getId(sport)} value={getId(sport)}>
													{sport?.name || "Discipline"}
												</option>
											))}
										</Form.Select>
									</Form.Group>
								</Col>
							</Row>
							<div className="mt-2 small text-muted">
								{filteredLeaguesForDrawer.length} league
								{filteredLeaguesForDrawer.length === 1 ? "" : "s"} match
							</div>
						</Card.Body>
					</Card>

					<Form.Group className="mb-3" controlId="prizeLeague">
						<Form.Label>League</Form.Label>
						<Form.Select
							value={form.league}
							onChange={(event) =>
								setForm((prev) => ({
									...prev,
									league: event.target.value,
									event: "",
								}))
							}
						>
							<option value="">Select league</option>
							{filteredLeaguesForDrawer.map((league) => (
								<option key={getId(league)} value={getId(league)}>
									{league?.name || "League"}
								</option>
							))}
						</Form.Select>
					</Form.Group>

					<Form.Group className="mb-3" controlId="prizeType">
						<Form.Label>Prize Type</Form.Label>
						<Form.Select
							value={form.prizeType}
							onChange={(event) =>
								setForm((prev) => ({
									...prev,
									prizeType: event.target.value as PrizeType,
									event:
										event.target.value === "round" ? prev.event : "",
								}))
							}
						>
							<option value="season">Season</option>
							<option value="round">Round</option>
						</Form.Select>
					</Form.Group>

					{form.prizeType === "round" ? (
						<Form.Group className="mb-3" controlId="prizeEvent">
							<Form.Label>Event</Form.Label>
							<Form.Select
								value={form.event}
								onChange={(event) =>
									setForm((prev) => ({ ...prev, event: event.target.value }))
								}
							>
								<option value="">Select event</option>
								{filteredEventsForLeague.map((eventItem) => (
									<option key={getId(eventItem)} value={getId(eventItem)}>
										{eventItem?.name || "Event"}{" "}
										{eventItem?.round ? `(Round ${eventItem.round})` : ""}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					) : null}

					<Form.Group className="mb-3" controlId="prizeTitle">
						<Form.Label>Title</Form.Label>
						<Form.Control
							maxLength={140}
							value={form.title}
							onChange={(event) =>
								setForm((prev) => ({ ...prev, title: event.target.value }))
							}
						/>
					</Form.Group>

					<Form.Group className="mb-3" controlId="prizeDescription">
						<Form.Label>Description</Form.Label>
						<Form.Control
							as="textarea"
							rows={4}
							maxLength={2000}
							value={form.description}
							onChange={(event) =>
								setForm((prev) => ({
									...prev,
									description: event.target.value,
								}))
							}
						/>
					</Form.Group>

					<Form.Group className="mb-3" controlId="prizeLinkUrl">
						<Form.Label>Link URL</Form.Label>
						<Form.Control
							value={form.linkUrl}
							onChange={(event) =>
								setForm((prev) => ({ ...prev, linkUrl: event.target.value }))
							}
							placeholder="https://..."
						/>
					</Form.Group>

					<Form.Group className="mb-2" controlId="prizeImageUpload">
						<Form.Label>Prize Image Upload</Form.Label>
						<Form.Control type="file" accept="image/*" onChange={handleUploadImage} />
					</Form.Group>
					<Form.Group className="mb-3" controlId="prizeImageUrl">
						<Form.Label>Prize Image URL</Form.Label>
						<Form.Control
							value={form.image.url}
							onChange={(event) =>
								setForm((prev) => ({
									...prev,
									image: { ...prev.image, url: event.target.value },
								}))
							}
						/>
					</Form.Group>

					<Form.Group className="mb-3" controlId="prizeWinnerSnapshot">
						<Form.Label>Winner Snapshot ID (Optional)</Form.Label>
						<Form.Control
							value={form.winnerSnapshot}
							onChange={(event) =>
								setForm((prev) => ({
									...prev,
									winnerSnapshot: event.target.value,
								}))
							}
						/>
					</Form.Group>

					<Form.Group className="mb-3" controlId="prizeWinnerLabel">
						<Form.Label>Winner Label (Optional)</Form.Label>
						<Form.Control
							value={form.winnerLabel}
							onChange={(event) =>
								setForm((prev) => ({
									...prev,
									winnerLabel: event.target.value,
								}))
							}
							placeholder="Winner name / team"
						/>
					</Form.Group>

					<Form.Check
						className="mb-3"
						label="Prize is active"
						checked={form.isActive}
						onChange={(event) =>
							setForm((prev) => ({ ...prev, isActive: event.target.checked }))
						}
					/>

					<div className="d-flex justify-content-between">
						<div>
							{selectedPrize?._id ? (
								<Button variant="outline-danger" onClick={handleDeactivate}>
									Deactivate
								</Button>
							) : null}
						</div>
						<div className="d-flex gap-2">
							<Button variant="outline-secondary" onClick={closeDrawer}>
								Cancel
							</Button>
							<Button onClick={handleSave} disabled={saving}>
								{saving ? "Saving..." : "Save"}
							</Button>
						</div>
					</div>
				</Offcanvas.Body>
			</Offcanvas>
		</AdminLayout>
	);
};

export default AdminPrizesPage;
